'use client'

import { useEffect, useRef } from 'react'
import { useAuth } from '@clerk/nextjs'
import {
  useQueryClient,
  type Query,
  type QueryCacheNotifyEvent,
  type QueryClient,
} from '@tanstack/react-query'
import { purgeOrgScopedStores } from '@/shared/lib/org-scoped-storage'
import { IDENTITY_QUERY_KEY, type Identity } from '@/lib/providers/identity'

/**
 * Which organisation this browser's persisted org-scoped stores were last
 * filled for. Read once per tab, on its first identity, so a page load into
 * another organisation (a bookmark into the other product) purges what the
 * previous visit persisted.
 */
const OWNER_STORAGE_KEY = 'cova-org-scoped-owner'

const readOwner = (): string | null => {
  try {
    return window.localStorage.getItem(OWNER_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeOwner = (orgId: string | null): void => {
  try {
    if (orgId === null) window.localStorage.removeItem(OWNER_STORAGE_KEY)
    else window.localStorage.setItem(OWNER_STORAGE_KEY, orgId)
  } catch {
    // Storage blocked: the in-tab comparison still runs.
  }
}

const isIdentityQuery = (query: Query): boolean =>
  IDENTITY_QUERY_KEY.every((part, i) => query.queryKey[i] === part)

type SeenIdentity = Pick<Identity, 'userId' | 'orgId'>

const identityOf = (query: Query): SeenIdentity | undefined =>
  query.state.data as SeenIdentity | undefined

/**
 * Drop every cached read from the previous organisation. Inactive entries
 * (the pages just left) are removed, so the next mount fetches; active ones
 * are refetched in place.
 *
 * Identity is keyed per scope, and the read that announced the change is
 * spared. So is every other scope's identity for the organisation now shown.
 * Another scope's identity for a different organisation is removed: left
 * cached and fresh, going back to that product would serve it with no fetch,
 * and its gate would render the page in the same commit, before any listener
 * here could purge. Removed, the way back reads it again, and its landing
 * purges first, as this one did.
 */
const forgetPreviousOrganisation = (queryClient: QueryClient, orgId: string | null): void => {
  const predicate = (query: Query) => !isIdentityQuery(query)
  queryClient.removeQueries({ type: 'inactive', predicate })
  void queryClient.invalidateQueries({ type: 'active', predicate })
  queryClient.removeQueries({
    type: 'inactive',
    predicate: (query) => isIdentityQuery(query) && identityOf(query)?.orgId !== orgId,
  })
}

/**
 * The cache events that can put an identity in front of the app. A read
 * landing is the one that matters: it fires before React is notified. A cached
 * entry becoming the one shown (an observer moving onto it) fires only after
 * the commit that rendered it, so it is the fallback for an entry the purge
 * above did not remove, never the first line.
 */
const showsIdentity = (event: QueryCacheNotifyEvent): boolean =>
  (event.type === 'updated' && event.action.type === 'success') ||
  event.type === 'observerAdded' ||
  event.type === 'observerResultsUpdated'

/**
 * Watches who the app is acting as, and purges org-scoped client state when
 * that changes.
 *
 * USER change (sign-out + sign-in as someone else, including a different test
 * account on the same browser): purges all org-scoped persisted Zustand stores
 * and reloads. Without this, per-org local caches (forms, submissions,
 * branding, etc.) leak across users sharing a browser — bug discovered
 * 2026-04-17 when tai+1@cova.ai signed in and saw a form that belonged to the
 * original tai@cova.ai account's org, with recipients silently failing because
 * the RPC's org filter (correctly) returned nothing.
 *
 * ORGANISATION change for the same user: the organisation is app-owned and
 * resolved per product (multi-tenancy-safety.md §1.5), so a person seated in
 * two organisations changes it with the product switcher, and accepting an
 * invitation changes it in place, without signing out. That purges the stores
 * and the root query cache's reads from the previous organisation, with no
 * reload: a product switch must not tear the app down.
 *
 * The organisation change is read off the root query cache, when an identity
 * read lands, not off `useIdentity()` in an effect. A section's gate renders
 * its page in the same commit the new identity reaches React, so an effect
 * would purge only after that page had rendered the previous organisation's
 * forms. A cache listener runs before React is notified.
 *
 * Mounted once in the root layout, inside QueryProvider and IdentityProvider.
 * Returns null — pure side effect.
 */
export function OrgChangeWatcher() {
  const { isLoaded, userId } = useAuth()
  const queryClient = useQueryClient()
  const previousUserIdRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (!isLoaded) return
    const currentUserId = userId ?? null

    // First load: just record the current user, don't purge.
    if (previousUserIdRef.current === undefined) {
      previousUserIdRef.current = currentUserId
      return
    }

    if (previousUserIdRef.current === currentUserId) return

    previousUserIdRef.current = currentUserId
    purgeOrgScopedStores()

    // Hard reload guarantees React Query, Zustand in-memory state, and any
    // module-level caches are rebuilt. User changes are infrequent, so the
    // disruption cost is acceptable for the safety guarantee.
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [userId, isLoaded])

  useEffect(() => {
    let previous: SeenIdentity | undefined

    return queryClient.getQueryCache().subscribe((event) => {
      if (!showsIdentity(event)) return
      // Only the identity being shown: the read for the scope the route names.
      if (!isIdentityQuery(event.query) || !event.query.isActive()) return
      const identity = identityOf(event.query)
      if (identity === undefined) return

      const last = previous
      // One purge per change: every later event for this identity is a repeat.
      if (last?.userId === identity.userId && last.orgId === identity.orgId) return
      previous = { userId: identity.userId, orgId: identity.orgId }

      if (last === undefined) {
        if (readOwner() !== identity.orgId) purgeOrgScopedStores()
      } else if (last.userId === identity.userId) {
        purgeOrgScopedStores()
        forgetPreviousOrganisation(queryClient, identity.orgId)
      }
      // A different user is the effect above's: it purges and reloads.
      writeOwner(identity.orgId)
    })
  }, [queryClient])

  return null
}
